import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  TransformWrapper, 
  TransformComponent, 
  ReactZoomPanPinchRef 
} from 'react-zoom-pan-pinch';
import type {
  PoleImageType,
  PoleAttachmentType,
  GuyAnchorType,
  Coordinate2D,
  Coordinate3D,
  PoleFormatOption,
} from '../types';
import { Controls } from './Controls';
import { ReferenceLine } from './ReferenceLine';
import { PoleAttachment } from './PoleAttachment';
import { DraggableReferenceLine } from './DraggableReferenceLine';
import { calculateXforY } from '../utils/calculations';

export interface PoleCanvasProps {
  // Required props
  imageUrl: string;
  poleImage: PoleImageType;
  attachments: PoleAttachmentType[];
  
  // Optional props
  guyAnchors?: GuyAnchorType[];
  measurementUnit?: PoleFormatOption;
  
  // State props
  selectedAttachment?: string;
  selectedGuyId?: string;
  scale?: number;
  pan?: Coordinate2D;
  isReferenceLineUpdateMode?: boolean;
  tempReferencePoints?: {
    top?: Coordinate3D;
    bottom?: Coordinate3D;
  };
  showGuyLines?: boolean;
  enableAddAttachments?: boolean; // Allow parent to enable/disable the feature
  // If true, clicking will add attachments without using internal toggle
  externalAddMode?: boolean;
  
  // Event handlers
  onAttachmentSelect?: (id: string | null) => void;
  onAttachmentUpdate?: (id: string, updates: Partial<PoleAttachmentType>) => void;
  onAttachmentAdd?: (attachment: Omit<PoleAttachmentType, 'id'>) => void;
  onBackgroundClick?: () => void;
  onScaleChange?: (scale: number) => void;
  onPanChange?: (pan: Coordinate2D) => void;
  onGuySelect?: (id: string) => void;
  onGuyHover?: (id: string | null) => void;
  onReferenceLineUpdate?: (top: Coordinate3D, bottom: Coordinate3D) => void;
  onReferencePointUpdate?: (point: 'top' | 'bottom', x: number, y: number) => void;
  onToggleReferenceLineUpdateMode?: () => void;
  onImageLoad?: (id: string, width: number, height: number) => void;
  onGenerateImage?: (generateFn: () => Promise<string>) => void;
  onLayoutChange?: () => void;
  
  // Configuration
  config?: {
    enableZoom?: boolean;
    enablePan?: boolean;
    enableDrag?: boolean;
    maxScale?: number;
    minScale?: number;
  };
  
  // Custom components
  components?: {
    controls?: React.ComponentType<any>;
    attachmentComponent?: React.ComponentType<any>;
    referenceLineComponent?: React.ComponentType<any>;
    guyAnchorComponent?: React.ComponentType<any>;
    loadingSpinner?: React.ComponentType;
  };
}

export const PoleCanvas: React.FC<PoleCanvasProps> = ({
  imageUrl,
  poleImage,
  attachments,
  guyAnchors = [],
  measurementUnit = 'metric',
  selectedAttachment,
  selectedGuyId,
  scale = 1,
  pan = { x: 0, y: 0 },
  isReferenceLineUpdateMode = false,
  tempReferencePoints,
  showGuyLines = true,
  enableAddAttachments = true,
  externalAddMode = false,
  onAttachmentSelect,
  onAttachmentUpdate,
  onAttachmentAdd,
  onBackgroundClick,
  onScaleChange,
  onPanChange,
  onGuySelect,
  onGuyHover,
  onReferenceLineUpdate,
  onReferencePointUpdate,
  onToggleReferenceLineUpdateMode,
  onImageLoad,
  onGenerateImage,
  onLayoutChange,
  config = {
    enableZoom: true,
    enablePan: true,
    enableDrag: true,
    maxScale: 12,
    minScale: 0.25,
  },
  components = {},
}) => {
  // Resolve a consistent image key used for matching attachment image records
  const currentImageKey = useMemo(() => {
    // Prefer the underlying file/image id if present, else fall back to the logical id
    return (poleImage as any).image || poleImage.id;
  }, [poleImage]);
  const [isLoading, setIsLoading] = useState(true);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [renderedSize, setRenderedSize] = useState({ width: 0, height: 0 });
  const [initialOffsets, setInitialOffsets] = useState({ horizontal: 0, vertical: 0 });
  const [transformState, setTransformState] = useState({ x: 0, y: 0, scale: 1 });
  const [isDragging, setIsDragging] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false); // Manage add mode internally
  
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const transformComponentRef = useRef<ReactZoomPanPinchRef | null>(null);
  
  const ControlsComponent = components.controls || Controls;
  const LoadingComponent = components.loadingSpinner || (() => <div style={{
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}>Loading...</div>);
  
  // Recalculate image dimensions and offsets
  const recalculateDimensions = useCallback(() => {
    if (imgRef.current && containerRef.current) {
      const img = imgRef.current;
      const container = containerRef.current;
      
      // Get the actual rendered size of the image element
      const imgRect = img.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      
      // Get natural dimensions
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      
      // Calculate the scale factor for object-fit: contain
      const containerWidth = img.offsetWidth;
      const containerHeight = img.offsetHeight;
      
      const scaleX = containerWidth / naturalWidth;
      const scaleY = containerHeight / naturalHeight;
      const scale = Math.min(scaleX, scaleY);
      
      // Calculate actual rendered dimensions
      const renderedWidth = naturalWidth * scale;
      const renderedHeight = naturalHeight * scale;
      
      // Calculate offsets (where the actual image starts within the img element)
      const horizontalOffset = (containerWidth - renderedWidth) / 2;
      const verticalOffset = (containerHeight - renderedHeight) / 2;
      
      setRenderedSize({
        width: renderedWidth,
        height: renderedHeight,
      });
      
      setInitialOffsets({
        horizontal: horizontalOffset,
        vertical: verticalOffset,
      });
    }
  }, []);

  // Handle image load
  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    
    if (imgRef.current) {
      const img = imgRef.current;
      
      const newNaturalSize = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };
      
      setNaturalSize(newNaturalSize);
      
      // Recalculate dimensions
      recalculateDimensions();
      
      // Call onImageLoad callback
      onImageLoad?.(poleImage.id, newNaturalSize.width, newNaturalSize.height);
      
      // Reset transform
      transformComponentRef.current?.resetTransform();
    }
  }, [poleImage.id, onImageLoad, recalculateDimensions]);
  
  // Calculate image scale - memoized to ensure consistency
  const imageScale = useMemo(() => {
    const widthScale = renderedSize.width / naturalSize.width;
    const heightScale = renderedSize.height / naturalSize.height;
    return Math.min(widthScale, heightScale);
  }, [renderedSize, naturalSize]);
  
  // Keep calculateScale for backward compatibility
  const calculateScale = useCallback(() => {
    return imageScale;
  }, [imageScale]);
  
  // Handle canvas click for adding attachments
  const handleCanvasClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) return;
    
    if (!containerRef.current || !imgRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const imgRect = imgRef.current.getBoundingClientRect();
    
    // Get click position relative to the container
    const clickX = event.clientX - containerRect.left;
    const clickY = event.clientY - containerRect.top;
    
    // Account for transform (zoom and pan)
    const transformedX = (clickX - transformState.x) / transformState.scale;
    const transformedY = (clickY - transformState.y) / transformState.scale;
    
    // Account for padding (20px on all sides from the flex container)
    const paddingAdjustedX = transformedX - 20;
    const paddingAdjustedY = transformedY - 20;
    
    // Now account for the image positioning within its container (object-fit: contain)
    const imageX = paddingAdjustedX - initialOffsets.horizontal;
    const imageY = paddingAdjustedY - initialOffsets.vertical;
    
    // Convert to natural image coordinates
    const naturalX = imageX / imageScale;
    const naturalY = imageY / imageScale;
    
    // Check if click is within image bounds
    if (naturalX < 0 || naturalY < 0 || 
        naturalX > naturalSize.width || naturalY > naturalSize.height) {
      onBackgroundClick?.();
      return;
    }
    
    if ((isAddMode || externalAddMode) && onAttachmentAdd) {
      onAttachmentAdd({
        type: 'equipment',
        position: { x: naturalX, y: naturalY },
        height: 0,
        label: 'New Attachment',
      });
      return;
    }
    
    // Check if clicked on an attachment
    const clickedAttachment = attachments.find(att => {
      const img = att.images?.find(i => i.imageId === currentImageKey);
      const attX = img?.point.x || att.position?.x || 0;
      const attY = img?.point.y || att.position?.y || 0;
      const dx = Math.abs(attX - naturalX);
      const dy = Math.abs(attY - naturalY);
      return dx < 20 && dy < 20; // 20px tolerance in natural coordinates
    });
    
    if (clickedAttachment) {
      onAttachmentSelect?.(clickedAttachment.id);
    } else {
      onBackgroundClick?.();
    }
  }, [
    isDragging,
    isAddMode,
    externalAddMode,
    attachments,
    transformState,
    initialOffsets,
    calculateScale,
    naturalSize,
    poleImage.id,
    onAttachmentAdd,
    onAttachmentSelect,
    onBackgroundClick,
  ]);
  
  // Toggle add mode
  const handleToggleAddMode = useCallback(() => {
    if (enableAddAttachments) {
      setIsAddMode(prev => !prev);
    }
  }, [enableAddAttachments]);
  
  // Handle transform changes
  const handleTransform = useCallback((e: any) => {
    const { positionX, positionY, scale } = e.state;
    setTransformState({ x: positionX, y: positionY, scale });
    onScaleChange?.(scale);
    onPanChange?.({ x: positionX, y: positionY });
  }, [onScaleChange, onPanChange]);
  
  // Mouse event handlers for drag detection
  const handleMouseDown = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  const handleMouseMove = useCallback(() => {
    setIsDragging(true);
  }, []);
  
  const handleMouseUp = useCallback(() => {
    setTimeout(() => setIsDragging(false), 0);
  }, []);
  
  // Generate image export function with full scaling and formatting
  const generateImage = useCallback(async (): Promise<string> => {
    if (!imageUrl || !imgRef.current) {
      throw new Error('Image not available');
    }
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Set canvas to natural image size
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        
        // Draw the base image
        ctx.drawImage(img, 0, 0);
        
        // Draw reference line (yellow line) using poleImage data
        if (poleImage.referenceLine) {
          const topPoint = poleImage.referenceLine.top.pixelCoordinates;
          const bottomPoint = poleImage.referenceLine.bottom.pixelCoordinates;
          
          ctx.strokeStyle = '#FFD700'; // Yellow color
          ctx.lineWidth = 16; // Extra thick line
          ctx.beginPath();
          ctx.moveTo(topPoint.x, topPoint.y);
          ctx.lineTo(bottomPoint.x, bottomPoint.y);
          ctx.stroke();
        }
        
        // Calculate label positions with collision detection
        // Use the same image key logic as the rest of the component
        const imageKey = (poleImage as any).image || poleImage.id;
        const attachmentData = attachments
          .map(att => {
            // Get the position for this specific image
            const imageData = att.images?.find(img => img.imageId === imageKey);
            if (!imageData) {
              // Fallback to position if no image-specific position
              if (!att.position) return null;
              return {
                attachment: att,
                originalY: att.position.y,
                dotX: att.position.x,
                dotY: att.position.y
              };
            }
            return {
              attachment: att,
              originalY: imageData.point.y,
              dotX: imageData.point.x,
              dotY: imageData.point.y
            };
          })
          .filter((data): data is { attachment: typeof attachments[0]; originalY: number; dotX: number; dotY: number } => data !== null)
          .sort((a, b) => a.originalY - b.originalY); // Sort top to bottom
        
        // Apply spacing logic to prevent overlaps
        const MIN_SPACING = 180; // Bigger spacing for potentially taller boxes
        const labelOffsets: Record<string, number> = {};
        let lastY = -Infinity;
        
        attachmentData.forEach(data => {
          let adjustedY = data.originalY;
          if (adjustedY < lastY + MIN_SPACING) {
            adjustedY = lastY + MIN_SPACING;
          }
          labelOffsets[data.attachment.id] = adjustedY - data.originalY;
          lastY = adjustedY;
        });
        
        // Helper function to format measurement (value is in meters)
        const formatMeasurement = (value: number): string => {
          if (measurementUnit === 'metric') {
            return `${value.toFixed(1)}m`;
          } else if (measurementUnit === 'feet_inches') {
            const totalFeet = value * 3.28084;
            const feet = Math.floor(totalFeet);
            const inches = Math.round((totalFeet - feet) * 12);
            return `${feet}'${inches}"`;
          } else {
            const feet = value * 3.28084;
            return `${feet.toFixed(1)}ft`;
          }
        };
        
        // Draw attachments with spacing adjustments
        attachmentData.forEach(data => {
          const { attachment, dotX, dotY } = data;
          const yOffset = labelOffsets[attachment.id] || 0;
          
          // Determine attachment color based on type (same logic as canvas)
          let dotColor = '#FF0000'; // Default red
          let boxColor = '#FFFFFF'; // Default white
          let textColor = '#000000'; // Default black text
          
          if (attachment.relatedGuys && attachment.relatedGuys.length > 0) {
            // Gold color for guy attachments
            dotColor = 'rgb(255, 215, 0)';
            boxColor = 'rgb(255, 215, 0)';
            textColor = '#000000';
          } else if (attachment.type === 'wire' || (attachment.relatedSpans && attachment.relatedSpans.length > 0)) {
            // Light blue for wire/span attachments
            dotColor = 'rgb(147, 197, 253)';
            boxColor = 'rgb(147, 197, 253)';
            textColor = '#000000';
          } else if (!attachment.classification || attachment.classification === '') {
            // Red for empty classification
            dotColor = '#FF0000';
            boxColor = '#FF0000';
            textColor = '#FFFFFF';
          } else {
            // White for normal attachments
            dotColor = '#FFFFFF';
            boxColor = '#FFFFFF';
            textColor = '#000000';
          }
          
          // Draw dot on the yellow line with appropriate color
          ctx.fillStyle = dotColor;
          ctx.beginPath();
          ctx.arc(dotX, dotY, 25, 0, 2 * Math.PI); // Large dot
          ctx.fill();
          
          // Get attachment display name
          let attachmentName = '';
          const hasNoName = !attachment.inventoryClass?.name && !attachment.classification;
          
          if (attachment.inventoryClass?.name) {
            attachmentName = attachment.inventoryClass.name;
          } else if (attachment.classification) {
            attachmentName = attachment.classification;
          } else if (attachment.label) {
            attachmentName = attachment.label;
          } else {
            attachmentName = 'select name';
          }
          
          // Add guy anchor info if related
          let guyInfo = '';
          if (attachment.relatedGuys && attachment.relatedGuys.length > 0) {
            const guyIds = attachment.relatedGuys.map((id: string) => id.slice(-4)).join(', ');
            guyInfo = ` (Guy: ${guyIds})`;
          }
          
          const displayName = attachmentName + guyInfo;
          const heightText = formatMeasurement(attachment.height);
          
          // Get subcategories
          const subcategories = attachment.inventoryClass?.subcategories || [];
          const subcategoryText = subcategories
            .filter(sub => sub.value)
            .map(sub => `${sub.name}: ${sub.value}`)
            .join(' • ');
          
          // Measure text to calculate box dimensions
          ctx.font = 'bold 60px Arial';
          const nameWidth = ctx.measureText(displayName).width;
          ctx.font = 'bold 50px Arial';
          const heightWidth = ctx.measureText(heightText).width;
          
          const minTitleWidth = nameWidth + heightWidth + 80;
          let subcategoryWidth = 0;
          if (subcategoryText) {
            ctx.font = 'bold 40px Arial';
            subcategoryWidth = ctx.measureText(subcategoryText).width + 60;
          }
          
          const boxWidth = Math.max(minTitleWidth, subcategoryWidth, 500);
          const baseHeight = 110;
          const subcategoryLines = subcategoryText ? 1 : 0; // Simplified for now
          const boxHeight = baseHeight + (subcategoryLines * 50);
          const boxX = img.naturalWidth - boxWidth - 40;
          const adjustedBoxY = (dotY + yOffset) - boxHeight / 2;
          const cornerRadius = 20;
          
          // Draw rounded rectangle helper
          const drawRoundedRect = (x: number, y: number, width: number, height: number, radius: number) => {
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.lineTo(x + width - radius, y);
            ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
            ctx.lineTo(x + width, y + height - radius);
            ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
            ctx.lineTo(x + radius, y + height);
            ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
            ctx.lineTo(x, y + radius);
            ctx.quadraticCurveTo(x, y, x + radius, y);
            ctx.closePath();
          };
          
          // Draw background box with appropriate color
          ctx.fillStyle = boxColor;
          drawRoundedRect(boxX, adjustedBoxY, boxWidth, boxHeight, cornerRadius);
          ctx.fill();
          
          // Draw border - darker version of box color or gray
          if (boxColor === '#FFFFFF') {
            ctx.strokeStyle = '#374151';
          } else {
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
          }
          ctx.lineWidth = 3;
          drawRoundedRect(boxX, adjustedBoxY, boxWidth, boxHeight, cornerRadius);
          ctx.stroke();
          
          // Draw connecting line from dot to box (use box color)
          ctx.strokeStyle = boxColor;
          ctx.lineWidth = 8;
          
          if (yOffset > 5) {
            // Curved line when offset
            const midX = dotX + (boxX - dotX) / 2;
            ctx.beginPath();
            ctx.moveTo(dotX, dotY);
            ctx.quadraticCurveTo(midX, dotY, boxX, adjustedBoxY + boxHeight / 2);
            ctx.stroke();
          } else {
            // Straight line
            ctx.beginPath();
            ctx.moveTo(dotX, dotY);
            ctx.lineTo(boxX, adjustedBoxY + boxHeight / 2);
            ctx.stroke();
          }
          
          // Draw text content with appropriate color
          ctx.fillStyle = textColor;
          
          // Height on the right
          ctx.font = 'bold 50px Arial';
          ctx.fillText(heightText, boxX + boxWidth - heightWidth - 30, adjustedBoxY + 60);
          
          // Name on the left
          ctx.font = 'bold 60px Arial';
          ctx.fillText(displayName, boxX + 30, adjustedBoxY + 60);
          
          // Subcategories (use slightly lighter version of text color)
          if (subcategoryText) {
            ctx.fillStyle = textColor === '#FFFFFF' ? '#E5E7EB' : '#6B7280';
            ctx.font = 'bold 40px Arial';
            ctx.fillText(subcategoryText, boxX + 30, adjustedBoxY + 120);
          }
        });
        
        // Draw guy anchors
        guyAnchors.forEach(guy => {
          if (guy.position) {
            const x = guy.position.x;
            const y = guy.position.y;
            
            // Draw yellow circle (matching canvas style)
            ctx.fillStyle = '#FACC15'; // yellow-400
            ctx.beginPath();
            ctx.arc(x, y, 25, 0, 2 * Math.PI);
            ctx.fill();
            
            // Draw yellow line from center to anchor
            ctx.strokeStyle = 'yellow';
            ctx.lineWidth = 12;
            ctx.beginPath();
            ctx.moveTo(img.naturalWidth / 2, img.naturalHeight / 2);
            ctx.lineTo(x, y);
            ctx.stroke();
          }
        });
        
        resolve(canvas.toDataURL('image/png'));
      };
      
      img.onerror = reject;
      img.src = imageUrl;
    });
  }, [imageUrl, poleImage, attachments, guyAnchors, measurementUnit]);
  
  // Pass generate function to parent
  useEffect(() => {
    if (onGenerateImage) {
      onGenerateImage(generateImage);
    }
  }, [onGenerateImage, generateImage]);
  
  // Handle container resize using ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Debounce the recalculation slightly
      setTimeout(recalculateDimensions, 50);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [recalculateDimensions]);

  // Handle layout changes (e.g., sidebar toggle)
  useEffect(() => {
    if (onLayoutChange) {
      onLayoutChange();
      // Recalculate after layout changes
      setTimeout(() => {
        recalculateDimensions();
      }, 300); // Small delay to allow CSS transitions to complete
    }
  }, [onLayoutChange, recalculateDimensions]);

  // Handle escape key to exit add mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isAddMode) {
        setIsAddMode(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAddMode]);
  
  // Calculate label offsets to prevent overlaps
  const labelOffsets = useMemo(() => {
    const zoomScale = transformState.scale;
    const offsets: Record<string, number> = {};
    if (!poleImage || attachments.length === 0) return offsets;

    // Build list of {id, containerY, screenY}
    const items = attachments
      .map(att => {
        // Get the position for this specific image
        const img = att.images?.find(i => i.imageId === poleImage.id);
        if (!img) {
          // Fallback to position if no image-specific position
          if (!att.position) return null;
          const containerY = att.position.y * imageScale;
          const screenY = (containerY * zoomScale) + transformState.y;
          return { id: att.id, containerY, screenY };
        }
        const containerY = img.point.y * imageScale;
        // convert to screen px by applying zoom & pan
        const screenY = (containerY * zoomScale) + transformState.y;
        return { id: att.id, containerY, screenY };
      })
      .filter(Boolean) as { id: string; containerY: number; screenY: number }[];
    
    if (items.length === 0) return offsets;
    
    // Sort by screenY to process top-to-bottom
    items.sort((a, b) => a.screenY - b.screenY);
    
    // Apply spacing logic to prevent overlaps in screen space
    const MIN_SPACING_SCREEN = 30; // px between label centres on screen
    let lastScreenY = -Infinity;
    
    items.forEach(item => {
      let screenOffset = 0;
      if (item.screenY < lastScreenY + MIN_SPACING_SCREEN) {
        screenOffset = lastScreenY + MIN_SPACING_SCREEN - item.screenY;
      }
      // Convert back into container-space offset (divide by zoomScale)
      offsets[item.id] = screenOffset / zoomScale;
      lastScreenY = item.screenY + screenOffset;
    });
    
    return offsets;
  }, [poleImage, attachments, imageScale, transformState, initialOffsets.vertical]);
  
  if (!imageUrl) {
    return <div style={{
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>No image available</div>;
  }
  
  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#9ca3af',  // Gray background
      }}
      onClick={handleCanvasClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {isLoading && <LoadingComponent />}
      
      <TransformWrapper
        initialScale={scale}
        initialPositionX={pan.x}
        initialPositionY={pan.y}
        minScale={config.minScale}
        maxScale={config.maxScale}
        disabled={isReferenceLineUpdateMode || (!config.enableZoom && !config.enablePan)}
        ref={transformComponentRef}
        onTransformed={handleTransform}
        limitToBounds={false}
        centerOnInit={false}  // Don't auto-center, we'll position manually
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <TransformComponent
              wrapperStyle={{
                width: '100%',
                height: '100%',
              }}
              contentStyle={{
                width: '100%',
                height: '100%',
              }}
            >
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                padding: '20px',
                boxSizing: 'border-box',
              }}>
                <div style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '100%',
                  height: '100%',
                }}>
                  <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="Pole inspection"
                  onLoad={handleImageLoad}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center center',
                    display: 'block',
                  }}
                  crossOrigin="anonymous"
                />
                
                {/* Overlay container positioned over the actual image area */}
                {!isLoading && renderedSize.width > 0 && (
                  <div style={{
                    position: 'absolute',
                    left: `${initialOffsets.horizontal}px`,
                    top: `${initialOffsets.vertical}px`,
                    width: `${renderedSize.width}px`,
                    height: `${renderedSize.height}px`,
                    pointerEvents: 'auto',
                  }}>
                    {/* Reference line (rotated div implementation) or draggable reference line */}
                    {isReferenceLineUpdateMode && poleImage.referenceLine ? (
                      <DraggableReferenceLine
                        poleImage={poleImage}
                        topPoint={(tempReferencePoints?.top as any)?.pixelCoordinates || poleImage.referenceLine.top.pixelCoordinates}
                        bottomPoint={(tempReferencePoints?.bottom as any)?.pixelCoordinates || poleImage.referenceLine.bottom.pixelCoordinates}
                        imageScale={imageScale}
                        containerTransform={transformState}
                        initialHorizontalOffset={initialOffsets.horizontal}
                        initialVerticalOffset={initialOffsets.vertical}
                        onTopPointDrag={(x, y) => onReferencePointUpdate?.('top', x, y)}
                        onBottomPointDrag={(x, y) => onReferencePointUpdate?.('bottom', x, y)}
                      />
                    ) : (
                    poleImage.referenceLine && 
                     poleImage.referenceLine.top && 
                     poleImage.referenceLine.bottom &&
                     !isReferenceLineUpdateMode && (() => {
                      const bottom = {
                        x: poleImage.referenceLine.bottom.pixelCoordinates.x * imageScale,
                        y: poleImage.referenceLine.bottom.pixelCoordinates.y * imageScale
                      };
                      const top = {
                        x: poleImage.referenceLine.top.pixelCoordinates.x * imageScale,
                        y: poleImage.referenceLine.top.pixelCoordinates.y * imageScale
                      };
                      
                      const dx = top.x - bottom.x;
                      const dy = top.y - bottom.y;
                      const length = Math.sqrt(dx * dx + dy * dy);
                      const angle = Math.atan2(dy, dx) * 180 / Math.PI;
                      
                      return (
                        <div
                          style={{
                            position: 'absolute',
                            left: `${bottom.x}px`,
                            top: `${bottom.y}px`,
                            width: `${length}px`,
                            height: `${4 / transformState.scale}px`,
                            backgroundColor: 'yellow',
                            transform: `rotate(${angle}deg)`,
                            transformOrigin: 'left center',
                            pointerEvents: 'auto',
                          }}
                        />
                      );
                    })())}
                    
                    {/* Attachments */}
                    {!isReferenceLineUpdateMode && attachments.map(attachment => {
                      const labelOffset = labelOffsets[attachment.id] || 0;
                      
                      
                      
                      return (
                        <PoleAttachment
                          key={attachment.id}
                          attachment={attachment}
                          currentImageId={currentImageKey}
                          initialImageScale={imageScale}
                          currentTransform={transformState}
                          initialHorizontalOffset={initialOffsets.horizontal}
                          initialVerticalOffset={initialOffsets.vertical}
                          imageContainerWidth={renderedSize.width}
                          containerWidth={containerRef.current?.clientWidth || 0}
                          measurementFormat={measurementUnit}
                          referenceLineTop={{
                            x: poleImage.referenceLine!.top.pixelCoordinates.x * imageScale,
                            y: poleImage.referenceLine!.top.pixelCoordinates.y * imageScale,
                          }}
                          referenceLineBottom={{
                            x: poleImage.referenceLine!.bottom.pixelCoordinates.x * imageScale,
                            y: poleImage.referenceLine!.bottom.pixelCoordinates.y * imageScale,
                          }}
                          onPositionChange={onAttachmentUpdate ? (attachmentId, imageId, newY) => {
                            // Calculate the X coordinate based on the reference line
                            if (poleImage.referenceLine) {
                              const newX = calculateXforY(
                                newY,
                                poleImage.referenceLine.bottom.pixelCoordinates,
                                poleImage.referenceLine.top.pixelCoordinates
                              );
                              
                              onAttachmentUpdate(attachmentId, { position: { x: newX, y: newY } });
                            } else {
                              // Fallback if no reference line
                              const imgForThis = attachment.images?.find(i => i.imageId === currentImageKey);
                              const currentPosition = imgForThis?.point || attachment.position || { x: 0, y: 0 };
                              onAttachmentUpdate(attachmentId, { position: { ...currentPosition, y: newY } });
                            }
                          } : undefined}
                          onClick={onAttachmentSelect ? (attachmentId) => onAttachmentSelect(attachmentId) : undefined}
                          labelYOffset={labelOffset}
                          opacity={selectedAttachment && selectedAttachment !== attachment.id ? 0.6 : 1}
                        />
                      );
                    })}
                    
                    {/* Guy anchors */}
                    {showGuyLines && !isReferenceLineUpdateMode && guyAnchors.map(guy => {
                      const x = (guy.position?.x || 0) * imageScale;
                      const y = (guy.position?.y || 0) * imageScale;
                      
                      return (
                        <div
                          key={guy.id}
                          style={{
                            position: 'absolute',
                            left: `${x}px`,
                            top: `${y}px`,
                            transform: `translate(-50%, -50%) scale(${1 / transformState.scale})`,
                            transformOrigin: 'center',
                          }}
                        >
                          <div
                            style={{
                              cursor: 'pointer',
                              borderRadius: '9999px',
                              borderWidth: '2px',
                              borderStyle: 'solid',
                              borderColor: '#facc15',
                              backgroundColor: selectedGuyId === guy.id ? '#facc15' : 'rgba(250, 204, 21, 0.5)',
                              width: '16px',
                              height: '16px',
                            }}
                            onClick={() => onGuySelect?.(guy.id)}
                            onMouseEnter={() => onGuyHover?.(guy.id)}
                            onMouseLeave={() => onGuyHover?.(null)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>
            </TransformComponent>
            
            {/* Controls */}
            <ControlsComponent
              scale={transformState.scale}
              onScaleChange={(newScale: number) => {
                // TODO: Implement zoom to specific scale
              }}
              onZoomIn={() => zoomIn()}
              onZoomOut={() => zoomOut()}
              onReset={() => resetTransform()}
              isAddMode={externalAddMode ? true : isAddMode}
              onToggleAddMode={externalAddMode ? undefined : (enableAddAttachments ? handleToggleAddMode : undefined)}
              isReferenceLineUpdateMode={isReferenceLineUpdateMode}
              onToggleReferenceLineUpdateMode={onToggleReferenceLineUpdateMode}
              onExport={async () => {
                const dataUrl = await generateImage();
                const link = document.createElement('a');
                link.download = 'pole-inspection.png';
                link.href = dataUrl;
                link.click();
              }}
            />
          </>
        )}
      </TransformWrapper>
    </div>
  );
};